import React, { FunctionComponent, useEffect, useState } from "react";

import MaterialTable from "@material-table/core";

import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Switch,
  Tooltip,
  useMediaQuery
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { useTheme } from "@material-ui/core/styles";
import useStyles from "./style";
import Typography from "@material-ui/core/Typography";
import { Segment } from "../../models/Segment";
import { isOk } from "../../result";
import { SegmentEditor } from "./components/SegmentEditor";
import { thunkFetchSegments } from "../../store/segments/actions";
import { AppState } from "../../store";
import { asList } from "../../hooks/useLoadIfEmpty";

import { ConfirmDialog } from "../../components/dialogs/ConfirmDialog";
import * as SegmentApi from "../../api/segmentApi";
import { Alert } from "@material-ui/lab";
import { SegmentUploadUsers } from "./components/SegmentUploadUsers";
import {
  AnnouncementOutlined,
  GroupOutlined,
  InfoOutlined,
  PersonOutline,
  WarningOutlined
} from "@material-ui/icons";
import StoreDetails from "../stores/StoreList/components/StoreDetails";
import { SegmentUserList } from "./components/SegmentUserList";
import { useI18n } from "../../hooks/useI18n";

export const SegmentOverview: FunctionComponent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const dispatch = useDispatch();
  const { t } = useI18n();
  const segments = asList<Segment>(
    useSelector<AppState, unknown>(s => s.segments)
  );

  const [state, setState] = useState({
    showUpsertSegment: false,
    showDeleteSegment: false,
    upsertSuccess: false,
    deleteSuccess: false,
    deleteError: false,
    uploadUsers: false,
    reload: false,
    editSegment: {} as Segment,
    segments: [] as Segment[]
  });

  useEffect(() => {
    dispatch(thunkFetchSegments());
  }, []);

  useEffect(() => {
    if (state.reload) {
      dispatch(thunkFetchSegments());
      setState({ ...state, reload: false });
    }
  }, [state.reload]);

  const onSegmentSave = () => {
    setState({
      ...state,
      showUpsertSegment: false,
      upsertSuccess: true,
      reload: true
    });
  };

  return (
    <>
      <MaterialTable
        style={{ marginBottom: 20 }}
        title={t("common.customerSegments")}
        columns={[
          {
            title: t("common.id"),
            field: "id",
            hidden: true,
            searchable: true
          },
          { title: t("common.name"), field: "name" },
          {
            title: t("common.usersInSegment"),
            field: "userCount",
            render: (rowData: any) => rowData.userCount
          }
        ]}
        isLoading={!segments}
        data={segments.length ? (segments as Segment[]) : []}
        detailPanel={[
          rowData => ({
            render: () => {
              return (
                <SegmentUserList
                  segmentId={rowData.id}
                  hasRules={rowData.rules && rowData.rules.length > 0}
                  onSave={onSegmentSave}
                />
              );
            }
          })
        ]}
        actions={[
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editSegment"),
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showUpsertSegment: true,
                editSegment: rowData
              });
            }
          },
          {
            icon: "delete",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.deleteSegment"),
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showDeleteSegment: true,
                editSegment: rowData
              });
            }
          },
          {
            icon: "add_circle",
            iconProps: {
              style: { color: theme.palette.primary.main, fontSize: "2rem" }
            },
            tooltip: "Add new segment",
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                showUpsertSegment: true,
                editSegment: {} as Segment
              });
            }
          }
        ]}
        options={{
          actionsColumnIndex: -1,
          detailPanelType: "single",
          actionsCellStyle: { whiteSpace: "nowrap" },
          pageSize: 20
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={state.showUpsertSegment}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <Typography variant="h6">
            {state.editSegment.id
              ? t("common.actions.editSegment")
              : t("common.actions.createSegment")}
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                showUpsertSegment: false,
                uploadUsers: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent className={classes.segmentDialogContent}>
          {!state.editSegment.id && (
            <>
              <Grid
                style={{
                  width: "240px",
                  marginBottom: 8
                }}
                component="label"
                container
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                <Grid item>{t("common.useRules")}</Grid>
                <Grid item>
                  <Switch
                    checked={state.uploadUsers}
                    onChange={() =>
                      setState({
                        ...state,
                        uploadUsers: !state.uploadUsers
                      })
                    }
                    size={"small"}
                  />
                </Grid>
                <Grid item>{t("common.uploadUsers")}</Grid>
              </Grid>
              <Divider
                style={{
                  marginBottom: 12
                }}
              />
            </>
          )}
          {(!state.editSegment.id && !state.uploadUsers) ||
          (state.editSegment && state.editSegment.rules) ? (
            <SegmentEditor onSave={onSegmentSave} segment={state.editSegment} />
          ) : (
            <SegmentUploadUsers
              onSave={onSegmentSave}
              segment={state.editSegment}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={state.showDeleteSegment}
        titleText={t("common.deleteSegment")}
        bodyText={t("common.confirmDeleteSegment", {
          name: state.editSegment.name
        })}
        confirmText={t("common.delete")}
        onConfirm={async () => {
          const result = await SegmentApi.remove(state.editSegment.id);

          if (!isOk(result)) {
            setState({ ...state, deleteError: true });
            return false;
          }

          setState({
            ...state,
            reload: true,
            showDeleteSegment: false,
            editSegment: {} as Segment,
            deleteSuccess: true
          });
          return true;
        }}
        onCancel={() => {
          setState({
            ...state,
            showDeleteSegment: false,
            editSegment: {} as Segment
          });
        }}
      >
        {state.deleteError && (
          <Alert severity="error">{t("common.failedToDeleteSegment")}</Alert>
        )}
      </ConfirmDialog>

      <Snackbar
        open={state.upsertSuccess || state.deleteSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            upsertSuccess: false,
            deleteSuccess: false
          });
        }}
      >
        <Alert severity={"success"}>
          {state.upsertSuccess
            ? t("common.segmentAddedUpdated")
            : t("common.segmentDeleted")}
        </Alert>
      </Snackbar>
    </>
  );
};
